import type {
  LDEvaluationDetail,
  LDFlagSet,
  LDInspectionFlagDetailsChangedHandler,
  LDInspectionFlagUsedHandler,
} from 'launchdarkly-js-sdk-common'

export const resolveMeticulousBooleanFlag = (flagKey: string, value: boolean): boolean => {
  const meticulous = typeof window === 'undefined' ? undefined : window.Meticulous?.context
  if (!meticulous) {
    return value
  }

  try {
    const override = meticulous.getFlagOverride(flagKey)
    const resolvedValue = override.overridden ? override.value === true || override.value === 'true' : value
    meticulous.recordFeatureFlag(flagKey, resolvedValue)
    return resolvedValue
  } catch (error) {
    console.error('[Meticulous] Failed to resolve feature flag:', flagKey, error)
    return value
  }
}

const recordFlagToMeticulous = (flagKey: string, value: unknown) => {
  if (typeof value === 'boolean') {
    resolveMeticulousBooleanFlag(flagKey, value)
    return
  }

  // Only record if Meticulous is loaded
  if (!window.Meticulous?.context || typeof value !== 'string') {
    return
  }

  try {
    window.Meticulous.context.recordFeatureFlag(flagKey, value)
  } catch (error) {
    // Fail silently - don't break app if Meticulous recording fails
    console.error('[Meticulous] Failed to record feature flag:', flagKey, error)
  }
}

/**
 * LaunchDarkly inspector that records flags when they're evaluated.
 * This runs on every flag evaluation (useFlags, variation, etc.)
 */
const flagUsedInspector: LDInspectionFlagUsedHandler = {
  type: 'flag-used',
  name: 'meticulous-flag-used',
  method: (flagKey: string, flagDetail: LDEvaluationDetail) => {
    recordFlagToMeticulous(flagKey, flagDetail.value)
  },
}

/**
 * LaunchDarkly inspector that records all flags when they're loaded/updated in bulk.
 * This captures the initial flag state when LaunchDarkly initializes or when flags are updated.
 */
const flagDetailsChangedInspector: LDInspectionFlagDetailsChangedHandler = {
  type: 'flag-details-changed',
  name: 'meticulous-flags-loaded',
  method: (flagValueMap: LDFlagSet) => {
    // Record all flags when they're loaded
    Object.entries(flagValueMap).forEach(([flagKey, flagDetail]) => {
      recordFlagToMeticulous(flagKey, flagDetail.value)
    })
  },
}

/**
 * Creates LaunchDarkly inspectors that automatically record all flag activity to Meticulous.
 * Includes both initial flag load and individual flag evaluations.
 */
export const createMeticulousInspectors = () => [flagUsedInspector, flagDetailsChangedInspector]
